import { useEffect } from 'react';
import { groupBy } from 'lodash';
import { ErrorCard } from '../../../DefaultCard';
import {
  SecurityUpdateListCard,
  ExploitabilityAssessment,
  Disclaimer,
  RevisionListView,
  MitigationView,
  WorkaroundView,
  AcknowledgementListView,
  FAQView,
  VulnTitleCard,
} from './section';
import { useTranslation } from 'react-i18next';
import { ExecutiveSummaryView } from './section/ExecutiveSummary';
import { PageNotFound } from 'component/layout';
import { useSeoKeywords } from 'component/content/seo';
import { MessageBar, MessageBarType } from '@fluentui/react';
import {
  useCveNumberFromVulnerabilityUrl,
  useVulnerabilitySingleApi,
} from '../../../../api/vulnerability';
import { useMsal } from '@azure/msal-react';
import {
  FeedbackFivePointEveryXQuarters,
  FullPage1600pxLayoutStack,
  useScrollToHashAfterLoad,
} from '@msrceng/component';
import { InteractionStatus } from '@azure/msal-browser';

/** UX and layout for Vulnerability details page */
export const VulnerabilityDetail = () => {
  const { inProgress } = useMsal();
  const isLoadingAuth = inProgress !== InteractionStatus.None;
  const cveNumber = useCveNumberFromVulnerabilityUrl();

  const { isLoading, data, error } = useVulnerabilitySingleApi(cveNumber);

  useSeoKeywords('vulnerability');
  const [t] = useTranslation(['vulnerability', 'title', 'translation'], {
    useSuspense: false,
  });

  useEffect(() => {
    if (data?.cveTitle) {
      document.title =
        t('CVE Details', {
          cveNumber,
          ns: 'title',
        }) +
        ' - ' +
        data?.cveTitle;
      //until they fix this eslint bug
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      document
        .querySelector('meta[name="description"]')
        ?.setAttribute?.(
          'content',
          data?.description?.substring(0, 800).replace(/<\/?p>/gi, '') + '...'
        );
    }
  }, [cveNumber, t, data]);

  // This page should scroll to hashed sections after load completes
  useScrollToHashAfterLoad(isLoading, data);

  const articleByType = groupBy(data?.articles, 'articleType');

  if (error && !data && !isLoading && !isLoadingAuth) {
    document.title = 'Not found - ' + document.title;
    return (
      <>
        <MessageBar messageBarType={MessageBarType.error}>
          {error?.message}
        </MessageBar>
        <PageNotFound />
      </>
    );
  }

  return (
    <FullPage1600pxLayoutStack
      data-testid='page-vulnerability-detail'
      id='page-vulnerability-detail'
    >
      <VulnTitleCard cveNumber={cveNumber} />
      <ExecutiveSummaryView description={data?.description} />
      <ExploitabilityAssessment
        enableShimmer={isLoading}
        exploited={data?.exploited}
        latestSoftwareRelease={data?.latestSoftwareRelease}
        publiclyDisclosed={data?.publiclyDisclosed}
      />
      <MitigationView
        articleList={articleByType?.Mitigation}
        isLoading={isLoading}
      />
      <WorkaroundView
        articleList={articleByType?.Workaround}
        isLoading={isLoading}
      />
      <FAQView articleList={articleByType?.FAQ} isLoading={isLoading} />
      <AcknowledgementListView cveNumber={cveNumber} />
      <SecurityUpdateListCard cveNumber={cveNumber} />
      <Disclaimer />
      <RevisionListView data={data?.revisions} enableShimmer={isLoading} />
      {error && <ErrorCard error={error} />}

      <FeedbackFivePointEveryXQuarters
        source='SUG-CVEDetails'
        quartersBetweenDismiss={1}
        quartersBetweenRequests={1}
      >
        {t('CSAT', { ns: 'translation' })}
      </FeedbackFivePointEveryXQuarters>
    </FullPage1600pxLayoutStack>
  );
};
